// https://www.figma.com/design/GHMeezX5jnpiDRS2dzaEXx/Simplifi%3A-Foundations?node-id=5071-144&m=dev

import type { TypographyVariant } from '@mui/material/Typography';

export default function createTypography() {
  /* font weight */
  // const light = 300;
  const regular = 400;
  const medium = 500;
  const semiBold = 600;
  // const bold = 700;
  // const extraBold = 800;
  // const black = 900;
  /* letter spacing */
  const letterSpacingWide = '0.1em';
  const lineHeightLong = 1.5;
  /* Typography Base */
  const displayBase = {
    fontFamily: 'Rubik',
    fontSize: '1.625rem',
  };
  const headlineBase = {
    fontFamily: 'Rubik',
    fontSize: '1.375rem',
  };
  const titleBase = {
    fontFamily: 'Rubik',
    fontSize: '1.125rem',
  };
  const bodyBase = {
    fontFamily: 'Rubik',
    fontSize: '1rem',
  };
  const paragraphBase = {
    fontFamily: 'Rubik',
    fontSize: '0.875rem',
  };
  const captionBase = {
    fontFamily: 'Rubik',
    fontSize: '0.75rem',
  };
  const overlineBase = {
    fontFamily: 'Rubik',
    fontSize: '0.75rem',
    letterSpacing: letterSpacingWide,
    textTransform: 'uppercase',
    lineHeight: lineHeightLong,
  };
  return {
    fontFamily: [
      'Rubik',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
      '"Segoe UI"',
      '-apple-system',
      'BlinkMacSystemFont',
    ].join(','),
    /* Display */
    displayRegular: {
      ...displayBase,
      fontWeight: regular,
    },
    displayMedium: {
      ...displayBase,
      fontWeight: medium,
    },
    displayBold: {
      ...displayBase,
      fontWeight: semiBold,
    },
    /* Headline */
    headlineRegular: {
      ...headlineBase,
      fontWeight: regular,
    },
    headlineMedium: {
      ...headlineBase,
      fontWeight: medium,
    },
    headlineBold: {
      ...headlineBase,
      fontWeight: semiBold,
    },
    /* Title */
    titleRegular: {
      ...titleBase,
      fontWeight: regular,
    },
    titleMedium: {
      ...titleBase,
      fontWeight: medium,
    },
    titleBold: {
      ...titleBase,
      fontWeight: semiBold,
    },
    /* Body */
    bodyRegular: {
      ...bodyBase,
      fontWeight: regular,
    },
    bodyMedium: {
      ...bodyBase,
      fontWeight: medium,
    },
    bodyBold: {
      ...bodyBase,
      fontWeight: semiBold,
    },
    /* Paragraph */
    paragraphRegular: {
      ...paragraphBase,
      fontWeight: regular,
    },
    paragraphMedium: {
      ...paragraphBase,
      fontWeight: medium,
    },
    paragraphBold: {
      ...paragraphBase,
      fontWeight: semiBold,
    },
    /* Caption */
    captionRegular: {
      ...captionBase,
      fontWeight: regular,
    },
    captionMedium: {
      ...captionBase,
      fontWeight: medium,
    },
    captionBold: {
      ...captionBase,
      fontWeight: semiBold,
    },
    /* Overline */
    overlineRegular: {
      ...overlineBase,
      fontWeight: regular,
    },
    overlineMedium: {
      ...overlineBase,
      fontWeight: medium,
    },
    overlineBold: {
      ...overlineBase,
      fontWeight: semiBold,
    },
    /** Equivalent to paragraphMedium, but we allow it to be its own typography. Overrides MUI button typography */
    button: { ...paragraphBase, fontWeight: medium },

    /* Direct mapping of styles to generic typography variants. Note - these might be in some vanilla JS files */
    // If a new Figma design is using one of these, ask the designer to make updates.
    h1: { ...displayBase, fontWeight: medium },
    h2: { ...displayBase, fontWeight: medium },
    h3: { ...displayBase, fontWeight: medium },
    h4: { ...displayBase, fontWeight: medium },
    h5: { ...headlineBase, fontWeight: medium },
    h6: { ...titleBase, fontWeight: medium },
    subtitle1: { ...titleBase, fontWeight: medium },
    subtitle2: { ...bodyBase, fontWeight: medium },
    body1: { ...bodyBase, fontWeight: regular },
    body2: { ...paragraphBase, fontWeight: regular },
    caption: { ...captionBase, fontWeight: regular },
  };
}

export const typographyVariantMappings = {
  // Headers
  displayBold: 'h1',
  displayMedium: 'h2',
  displayRegular: 'h3',
  headlineBold: 'h4',
  headlineMedium: 'h5',
  headlineRegular: 'h6',

  // Paragraphs
  titleRegular: 'p',
  titleMedium: 'p',
  titleBold: 'p',
  bodyRegular: 'p',
  bodyMedium: 'p',
  bodyBold: 'p',
  paragraphRegular: 'p',
  paragraphMedium: 'p',
  paragraphBold: 'p',
  captionRegular: 'span',
  captionMedium: 'span',
  captionBold: 'span',
  overlineRegular: 'span',
  overlineMedium: 'span',
  overlineBold: 'span',
  button: 'p',

  // https://mui.com/material-ui/react-typography/#changing-the-semantic-element
  // @ts-expect-error Map internal defaults to there default values. These are also still used in some vanilla JS files
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
} as const satisfies Record<TypographyVariant, string>;
